import { useState } from 'react';
import { PASSWORD_MIN_LENGTH, checkPassword, passwordAcceptable } from '@forecastify/shared';
import {
  Button,
  Icon,
  SKELETON_WIDTHS,
  Skeleton,
  TextInput,
  Tooltip,
  skeletonWidth,
} from '@forecastify/ui';
import { useI18n, useT, type MessageKey } from '../../i18n';
import { relativeTime } from '../relative-time';
import { deviceIcon, deviceName } from './device-icon';
import { Card, CardColumn, Field } from '../card';
import { TwoFactorDialog, type TwoFactorEnrolment, type TwoFactorStage } from './two-factor-dialog';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Security — password, two-factor, passkeys, devices.                       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The password checklist renders the REAL policy**, from `PASSWORD_RULES` in
 * `@forecastify/shared`, which a test holds in step with `config.toml`. The mockup's own checklist
 * asks for ten characters and `/[a-zA-Z]/`; the configured policy is twelve with lower, upper and a
 * digit. Shipping the mockup's numbers would put a green tick beside a rule the server then rejects,
 * which makes the form look broken rather than the password wrong (`hard-won.md` C6-5 for the wider
 * story — GoTrue does not even check the current password, so our own check is the only one).
 */

export interface PasskeySummaryView {
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
  readonly lastUsedAt: string | null;
}

export interface SessionView {
  readonly sessionId: string;
  readonly userAgent: string | null;
  readonly ip: string | null;
  readonly lastSeenAt: string;
  readonly current: boolean;
}

export interface SecurityHandlers {
  /** Until the first read lands: the passkey and device lists draw skeleton rows. */
  readonly loading?: boolean;
  readonly onChangePassword?: (current: string, next: string) => Promise<void>;
  readonly passkeys?: readonly PasskeySummaryView[];
  /**
   * ⚠️ `name` is `''` when the person did not type one, which means *name it from the
   * authenticator* — item 17. `hint` is what the browser could work out, used only when the AAGUID
   * is one the server's table does not carry.
   */
  readonly onAddPasskey?: (name: string, hint: string) => Promise<void>;
  readonly onRemovePasskey?: (id: string) => Promise<void>;
  readonly sessions?: readonly SessionView[];
  readonly onRevokeSession?: (id: string) => Promise<void>;
  readonly onRevokeOthers?: () => Promise<void>;
  /**
   * ⚠️⚠️ **SUPPLIED AT LAST — item 11.** This prop has existed since C6 and no route ever passed
   * it, so the card offered *Enable* to somebody who already had two-factor on, every reload,
   * with no way to find out but to try. `GET /auth/totp` is where it comes from now.
   */
  readonly twoFactorEnabled?: boolean;
  /** ⚠️ How many unused recovery keys are left, so somebody can regenerate before running out. */
  readonly recoveryCodesLeft?: number;
  readonly onEnrolTotp?: () => Promise<TwoFactorEnrolment>;
  /** ⚠️ Answers with the recovery keys, which exist in readable form nowhere else. */
  readonly onVerifyTotp?: (code: string) => Promise<readonly string[]>;
  readonly onDisableTotp?: (password: string) => Promise<void>;
  readonly onRegenerateRecoveryCodes?: (password: string) => Promise<readonly string[]>;
}

const RULE_LABEL: Readonly<Record<string, MessageKey>> = {
  length: 'account.password.rule.length',
  letters: 'account.password.rule.letters',
  digits: 'account.password.rule.digits',
  match: 'account.password.rule.match',
};

/**
 * ⚠️ **Whether this browser can do WebAuthn at all**, checked before offering the button. A passkey
 * control that opens nothing on an unsupported browser reads as a broken feature; saying so, and
 * pointing at the password, is the honest version.
 */
function RowSkeletons(): React.JSX.Element {
  const t = useT();
  return (
    <>
      <ul aria-hidden className="flex flex-col">
        {SKELETON_WIDTHS.slice(0, 3).map((_widths, index) => (
          <li
            key={`skeleton-${String(index)}`}
            className="flex items-center gap-2.5 border-b py-2 last:border-0"
          >
            <Skeleton className="size-3.5 shrink-0" />
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <Skeleton width={skeletonWidth(index, 0)} />
              <Skeleton width={skeletonWidth(index, 1) / 2} className="h-2.5" />
            </div>
            <Skeleton width="72px" className="h-8 shrink-0 rounded-md" />
          </li>
        ))}
      </ul>
      <div role="status" aria-live="polite" className="sr-only">
        {t('state.loading')}
      </div>
    </>
  );
}

function passkeysSupported(): boolean {
  if (typeof window === 'undefined') return false;

  /* ⚠️ Read through a widened global rather than with `?.`: the DOM types promise `navigator.
   * credentials` exists, and it does not in jsdom or on an http origin that is not localhost — so
   * the optional chain the runtime needs is the one the types call unnecessary. Same shape as
   * `navigator.languages` in `i18n/index.tsx`. */
  const nav = navigator as unknown as { credentials?: { create?: unknown } };
  return (
    typeof window.PublicKeyCredential === 'function' &&
    typeof nav.credentials?.create === 'function'
  );
}

/** The refusals `POST /auth/password` answers with, by envelope `code`. Anything else is unknown. */
const PASSWORD_REFUSAL: Readonly<Record<string, MessageKey>> = {
  invalid_current_password: 'account.password.wrongCurrent',
  same_password: 'account.password.same',
  weak_password: 'account.password.weak',
  rate_limited: 'signIn.rateLimited',
  unavailable: 'error.unavailable',
};

export function SecuritySection({ handlers }: { readonly handlers?: SecurityHandlers }) {
  const t = useT();
  const { locale } = useI18n();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [changing, setChanging] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const [addingPasskey, setAddingPasskey] = useState(false);

  /**
   * ⚠️⚠️ **ONE PIECE OF STATE FOR THE WHOLE FLOW, BECAUSE THE STAGES ARE EXCLUSIVE.** Enrolling,
   * showing the keys, disabling and regenerating are four things that can never be true at once,
   * and four booleans would let two of them be — which is how a dialog comes to render a password
   * field over a QR code.
   */
  const [flow, setFlow] = useState<{
    stage: TwoFactorStage;
    enrolment?: TwoFactorEnrolment | null;
    keys?: readonly string[];
  } | null>(null);
  const [totpBusy, setTotpBusy] = useState(false);
  const [totpError, setTotpError] = useState<string | null>(null);

  const rules = checkPassword(next, confirmation);
  const ready = passwordAcceptable(next, confirmation) && current !== '';
  const supported = passkeysSupported();

  return (
    <CardColumn>
      {/* ── password ─────────────────────────────────────────────────────────────────────── */}
      <Card title={t('account.password.title')}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('account.password.current')}>
            <TextInput
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(event) => {
                setCurrent(event.target.value);
              }}
            />
          </Field>
          <Field label={t('account.password.next')}>
            <TextInput
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(event) => {
                setNext(event.target.value);
              }}
            />
          </Field>
          <Field label={t('account.password.confirm')}>
            <TextInput
              type="password"
              autoComplete="new-password"
              value={confirmation}
              onChange={(event) => {
                setConfirmation(event.target.value);
              }}
            />
          </Field>
        </div>

        {/* ⚠️ A list, ticked individually, and each rule is its own line — one combined predicate
            would render as a single "no" that does not say which part is missing. */}
        <ul className="mt-3 flex flex-col gap-1">
          {(Object.keys(rules) as (keyof typeof rules)[]).map((rule) => (
            <li key={rule} className="flex items-center gap-1.5 text-[11.5px]">
              <Icon
                name={rules[rule] ? 'circle-check' : 'circle'}
                size={13}
                className={rules[rule] ? 'text-primary' : 'text-muted-foreground'}
              />
              <span className={rules[rule] ? '' : 'text-muted-foreground'}>
                {t(RULE_LABEL[rule] ?? 'app.name', { min: PASSWORD_MIN_LENGTH })}
              </span>
            </li>
          ))}
        </ul>

        <Button
          className="mt-3"
          size="sm"
          disabled={!ready || changing}
          onClick={() => {
            if (handlers?.onChangePassword === undefined) return;
            setChanging(true);
            setPasswordError(null);
            void handlers
              .onChangePassword(current, next)
              .then(() => {
                setCurrent('');
                setNext('');
                setConfirmation('');
              })
              .catch((error: unknown) => {
                /* ⚠️ A refusal keeps all three fields: the person retypes the one that was wrong,
                   not the two that were fine. */
                const code = error instanceof Error ? error.message : '';
                setPasswordError(t(PASSWORD_REFUSAL[code] ?? 'error.unknown'));
              })
              .finally(() => {
                setChanging(false);
              });
          }}
        >
          {changing ? t('account.saving') : t('account.password.submit')}
        </Button>
        {passwordError !== null && (
          <p role="alert" className="text-destructive mt-2 text-[11.5px]">
            {passwordError}
          </p>
        )}
      </Card>

      {/* ── two-factor ───────────────────────────────────────────────────────────────────── */}
      <Card
        title={t('account.twofa.title')}
        hint={
          handlers?.twoFactorEnabled === true
            ? t('account.twofa.onNote')
            : t('account.twofa.offNote')
        }
        actions={
          handlers?.twoFactorEnabled === true ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setTotpError(null);
                  setFlow({ stage: 'regenerate' });
                }}
              >
                {t('account.twofa.keys.regenerate')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setTotpError(null);
                  setFlow({ stage: 'disable' });
                }}
              >
                {t('account.twofa.disable')}
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setTotpError(null);
                /* ⚠️ The dialog opens FIRST, with its skeleton, and the enrolment lands into
                   it. Waiting for the round trip before opening leaves a button that looks
                   broken for as long as GoTrue takes to answer. */
                setFlow({ stage: 'enrol', enrolment: null });
                void handlers?.onEnrolTotp?.().then(
                  (enrolment) => {
                    setFlow({ stage: 'enrol', enrolment });
                  },
                  () => {
                    setFlow(null);
                    setTotpError(t('error.unknown'));
                  },
                );
              }}
            >
              {t('account.twofa.enable')}
            </Button>
          )
        }
      >
        {/**
         * ⚠️⚠️ **THE COUNT IS A WARNING WHEN IT IS LOW, AND `state-rules.md` SS5 IS WHY IT IS
         * NEVER JUST A NUMBER.** Somebody with one key left is one lost phone from an account
         * nobody can open; the number alone does not say so, and the moment to say it is before
         * the last key is spent rather than after.
         */}
        {handlers?.twoFactorEnabled === true && (
          <div className="flex flex-col gap-2">
            {/**
             * ⚠️⚠️ **THE STATE IS A BADGE, NOT A SENTENCE — and a sentence is what was reported as
             * *"hard to read"*.** *"Signing in asks for a six-digit code after your password"* is a
             * description of the feature; it is muted 12px prose beside two buttons, and it reads
             * the same whether the thing is on or off. Somebody who has just finished the enrolment
             * wants one thing confirmed, and a tick with the word ON confirms it at a glance.
             *
             * ⚠️ **`--status-ok-text`, not `--status-ok`.** The first is the readable-on-the-page
             * variant the token layer computes; the second is the FILL, meant to sit under
             * `--on-*` ink. Using a fill colour as type is how a status line comes out at 2:1 in
             * one of the eight themes, which is the contrast audit's whole subject.
             */}
            <span className="flex items-center gap-1.5 text-[12.5px] font-medium text-[var(--status-ok-text)]">
              <Icon name="check" size={14} />
              {t('account.twofa.on')}
            </span>

            <p
              className={
                (handlers.recoveryCodesLeft ?? 0) <= 2
                  ? 'text-destructive text-[12px]'
                  : 'text-muted-foreground text-[12px]'
              }
            >
              {t('account.twofa.keys.left', { count: String(handlers.recoveryCodesLeft ?? 0) })}
            </p>
          </div>
        )}

        {totpError !== null && flow === null && (
          <p role="alert" className="text-destructive mt-2 text-[11.5px]">
            {totpError}
          </p>
        )}
      </Card>

      {flow !== null && (
        <TwoFactorDialog
          stage={flow.stage}
          enrolment={flow.enrolment}
          recoveryCodes={flow.keys}
          busy={totpBusy}
          error={totpError}
          onClose={() => {
            setFlow(null);
            setTotpError(null);
          }}
          onVerify={(code) => {
            setTotpBusy(true);
            setTotpError(null);
            void handlers?.onVerifyTotp?.(code).then(
              (keys) => {
                setTotpBusy(false);
                /* ⚠️ Straight on to the keys, never back to the card: this is the only moment
                   they exist, and a dialog that closed here would have thrown them away. */
                setFlow({ stage: 'keys', keys });
              },
              () => {
                setTotpBusy(false);
                setTotpError(t('account.twofa.wrongCode'));
              },
            );
          }}
          onDisable={(password) => {
            setTotpBusy(true);
            setTotpError(null);
            void handlers?.onDisableTotp?.(password).then(
              () => {
                setTotpBusy(false);
                setFlow(null);
              },
              () => {
                setTotpBusy(false);
                setTotpError(t('account.twofa.wrongPassword'));
              },
            );
          }}
          onRegenerate={(password) => {
            setTotpBusy(true);
            setTotpError(null);
            void handlers?.onRegenerateRecoveryCodes?.(password).then(
              (keys) => {
                setTotpBusy(false);
                setFlow({ stage: 'keys', keys });
              },
              () => {
                setTotpBusy(false);
                setTotpError(t('account.twofa.wrongPassword'));
              },
            );
          }}
        />
      )}

      {/* ── passkeys ─────────────────────────────────────────────────────────────────────── */}
      <Card
        title={t('account.passkeys.title')}
        actions={
          supported ? (
            <Button
              variant="outline"
              size="sm"
              disabled={addingPasskey}
              onClick={() => {
                /**
                 * ┌───────────────────────────────────────────────────────────────────────────┐
                 * │ ⚠️⚠️ THE BUTTON IS THE WHOLE FLOW — item 17, third pass, 15 Sep.          │
                 * └───────────────────────────────────────────────────────────────────────────┘
                 *
                 * *"It should be add passkey first, where the user connects the OS's passkey
                 * management, and then that gets saved automatically with a name."*
                 *
                 * The two earlier passes both kept a name field in front of the ceremony — first
                 * pre-filled, then empty — and that was the thing to remove, not to improve. A
                 * passkey is created by the operating system; asking somebody to name a thing that
                 * does not exist yet, before the only step that can fail or be cancelled, is a form
                 * standing in front of a button.
                 *
                 * ⚠️ **Nothing is lost by dropping it.** The server names the credential from the
                 * authenticator in the attestation it verifies (`authenticators.ts`), which is a
                 * better name than a person would type, and the row can be renamed afterwards —
                 * `PATCH /auth/passkeys/:id` has existed since C18.
                 *
                 * ⚠️ **`addingPasskey` is now *the prompt is open*, not *the form is open*.** The
                 * button disables for the second or two the browser owns, so a second click cannot
                 * start a second ceremony against a challenge the first one has consumed.
                 */
                setAddingPasskey(true);
                void handlers?.onAddPasskey?.('', deviceLabel()).finally(() => {
                  setAddingPasskey(false);
                });
              }}
            >
              {t(addingPasskey ? 'account.passkeys.adding' : 'account.passkeys.add')}
            </Button>
          ) : undefined
        }
      >
        {!supported && (
          <p className="text-muted-foreground text-[12px]">{t('account.passkeys.unsupported')}</p>
        )}

        {handlers?.loading === true ? (
          <RowSkeletons />
        ) : (handlers?.passkeys ?? []).length === 0 ? (
          supported && (
            <p className="text-muted-foreground text-[12px]">{t('account.passkeys.empty')}</p>
          )
        ) : (
          <ul className="flex flex-col">
            {(handlers?.passkeys ?? []).map((passkey) => (
              <li
                key={passkey.id}
                className="flex items-center gap-2.5 border-b py-2 last:border-0"
              >
                <Icon name="key" size={14} className="text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  {/* ⚠️ Item 14 · the full name on hover — a passkey named after a device is
                      often longer than the row. */}
                  <Tooltip asChild overflowOnly content={passkey.name}>
                    <p className="truncate text-[12.5px]">{passkey.name}</p>
                  </Tooltip>
                  {/* ⚠️ Item 12 · in words. A passkey nobody has used yet says so instead. */}
                  <p className="text-muted-foreground text-[11px]">
                    {t('account.passkeys.lastUsed', {
                      when:
                        passkey.lastUsedAt === null
                          ? t('account.passkeys.neverUsed')
                          : relativeTime(passkey.lastUsedAt, locale),
                    })}
                  </p>
                </div>
                {/* ⚠️ A neutral outline, not a filled danger button. The design system reserves the
                    loud one for irreversible financial deletions — deleting a passkey is not that,
                    and the loudest control on this card must never be the destructive one. */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void handlers?.onRemovePasskey?.(passkey.id);
                  }}
                >
                  {t('account.passkeys.remove')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* ── devices ──────────────────────────────────────────────────────────────────────── */}
      <Card
        title={t('account.sessions.title')}
        actions={
          (handlers?.sessions ?? []).length > 1 ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void handlers?.onRevokeOthers?.();
              }}
            >
              {t('account.sessions.revokeOthers')}
            </Button>
          ) : undefined
        }
      >
        {handlers?.loading === true ? (
          <RowSkeletons />
        ) : (
          <ul className="flex flex-col">
            {(handlers?.sessions ?? []).map((session) => (
              <li
                key={session.sessionId}
                className="flex items-center gap-2.5 border-b py-2 last:border-0"
              >
                {/* ⚠️ Item 13 · the device's own glyph. Every row drew `monitor`, so a phone and a
                  laptop were the same picture — which teaches the eye to stop reading it, on the
                  one list that exists so somebody can spot the device that is not theirs. */}
                <Icon
                  name={deviceIcon(session.userAgent)}
                  size={14}
                  className="text-muted-foreground shrink-0"
                />
                <div className="min-w-0 flex-1">
                  {/* ⚠️ Item 14 · **the full agent as a `title`.** A user agent is two hundred
                    characters of version numbers; the row shows the browser and the platform and
                    the tooltip keeps the rest, which is the detail that says WHICH laptop. */}
                  <Tooltip asChild content={session.userAgent}>
                    <p className="truncate text-[12.5px]">
                      {deviceName(session.userAgent) ?? session.userAgent ?? t('error.unknown')}
                      {session.current && (
                        <span className="text-muted-foreground">
                          {' '}
                          · {t('account.sessions.current')}
                        </span>
                      )}
                    </p>
                  </Tooltip>
                  {/* ⚠️ Item 12 · *"2 órája"*, not an ISO instant. `relative-time.ts` carries it. */}
                  <p className="text-muted-foreground text-[11px]">
                    {session.ip ?? '–'} ·{' '}
                    {t('account.sessions.lastSeen', {
                      when: relativeTime(session.lastSeenAt, locale),
                    })}
                  </p>
                </div>
                {/* ⚠️ No control for the current device: "sign out" on the session you are using is
                  a sign-out, not a device revocation, and it belongs in the user menu. */}
                {!session.current && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      void handlers?.onRevokeSession?.(session.sessionId);
                    }}
                  >
                    {t('account.sessions.revoke')}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </CardColumn>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ A DEFAULT NAME FOR A PASSKEY. THE BROWSER DOES NOT KNOW THE DEVICE'S │
 * │ NAME, AND NOTHING HERE PRETENDS OTHERWISE.                                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Asked for on 14 Sep: *"check if it's possible to automatically name the passkey based on the
 * device's name"*. It is not — no web API exposes *"Adrian's MacBook Pro"*, deliberately, because
 * that is a fingerprinting surface. What is available:
 *
 * 1. **The platform and browser**, from `navigator.userAgentData` where it exists and the
 *    user-agent string where it does not. That is what this returns: *"Windows · Chrome"*.
 * 2. ⚠️⚠️ **The AAGUID on the attestation, WHICH IS NOW WHAT ACTUALLY NAMES A PASSKEY** — item 17,
 *    second pass. It names the AUTHENTICATOR rather than the machine: *iCloud Keychain*, *Windows
 *    Hello*, *1Password*. The note here used to say that was the upgrade if this proved too vague,
 *    and somebody did ask twice; `apps/api/src/auth/authenticators.ts` is the table.
 *
 * ⚠️⚠️ **SO THIS IS THE FALLBACK AND THE PLACEHOLDER, NOT THE VALUE.** It cannot be the value:
 * the AAGUID does not exist until the ceremony has happened, which is after this field is filled
 * in. What it is good for is the case the table misses — the server takes it as a hint and uses it
 * only then.
 */
function deviceLabel(): string {
  const agent = navigator.userAgent;

  /* ⚠️ Read through `unknown`: `userAgentData` is Chromium-only and the DOM types do not carry it,
     so a direct access is a type error and a cast is a lie in Safari. */
  const hinted = (globalThis as { navigator?: { userAgentData?: { platform?: unknown } } })
    .navigator?.userAgentData?.platform;
  /* ⚠️ `find`, not a ladder of ternaries: the ORDER is the whole logic in both tables, and a list
     says so where nested conditionals bury it. */
  const platform =
    typeof hinted === 'string' && hinted !== ''
      ? hinted
      : ((
          [
            ['Windows', 'Windows'],
            ['Macintosh', 'macOS'],
            ['Android', 'Android'],
            ['iPhone', 'iOS'],
            ['iPad', 'iOS'],
            ['Linux', 'Linux'],
          ] as const
        ).find(([needle]) => agent.includes(needle))?.[1] ?? null);

  /* ⚠️ Order matters: every Chromium browser says `Chrome` and Safari's string contains it too,
     so the specific names are tested before the general one. */
  const browser =
    (
      [
        ['Edg/', 'Edge'],
        ['OPR/', 'Opera'],
        ['Firefox/', 'Firefox'],
        ['Chrome/', 'Chrome'],
        ['Safari/', 'Safari'],
      ] as const
    ).find(([needle]) => agent.includes(needle))?.[1] ?? null;

  if (platform !== null && browser !== null) return `${platform} · ${browser}`;
  return platform ?? browser ?? '';
}
